import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import BaseWidget from '../BaseWidget';
import { ForecastChartComponent } from '../../ForecastChartComponent.jsx';
import { formatSeriesName } from '../../Utils.js';
import useWidgetData from '../hooks/useWidgetData';
import _ from 'underscore';
import dayjs from 'dayjs';

/**
 * ForecastChartWidget - Shows forecast data in a chart view only
 * Uses ForecastChartComponent with forecast support, no toggle buttons
 * Supports series selection to filter which series are displayed
 */
const ForecastChartWidget = ({ 
  widgetId, 
  config, 
  filters = [], 
  onRemove, 
  isEditMode = false,
  onFilterAdd
}) => {
  const { data, isLoading, error } = useWidgetData(config, filters);
  const [dateGrouping, setDateGrouping] = useState(
    config.dateGrouping || { label: "Day", value: "MMM D" }
  );

  // Transform data to match ForecastChartComponent expected format
  const allData = React.useMemo(() => {
    if (!data || !data.CloudCost || !data.CloudCost.details) {
      return [];
    }

    // Transform details to match ForecastChartComponent format
    const details = data.CloudCost.details.map((d) => {
      // Handle both API response format and already transformed format
      if (d.groupings && Array.isArray(d.groupings)) {
        return {
          time: d.groupings[0]?.value || d.time,
          grouping: d.groupings[1]?.value || d.grouping || 'Total',
          measure: d.measures?.[0]?.value ?? d.measure ?? 0,
          forecast: d.measures?.[1]?.value ?? d.forecast ?? 0
        };
      }
      // Already transformed format
      return {
        time: d.time,
        grouping: d.grouping || 'Total',
        measure: d.measure ?? 0,
        forecast: d.forecast ?? 0
      };
    });

    return details;
  }, [data]);

  // Extract available series (groupings) from data
  const availableSeries = useMemo(() => {
    if (!allData || allData.length === 0) return [];
    
    const grouped = _.groupBy(allData, "grouping");
    const series = Object.keys(grouped);
    
    // Sort by total measure value (descending)
    return series.sort((a, b) => {
      const sumA = grouped[a].reduce((acc, curr) => acc + (curr.measure || 0) + (curr.forecast || 0), 0);
      const sumB = grouped[b].reduce((acc, curr) => acc + (curr.measure || 0) + (curr.forecast || 0), 0);
      return sumB - sumA;
    });
  }, [allData]);


  // Get selected series from config, default to first available if not specified
  // Use a ref to track the last valid series to prevent flickering during data updates
  const lastValidSelectedSeriesRef = React.useRef(null);
  
  const selectedSeries = useMemo(() => {
    // Always prioritize config.selectedSeries if it's explicitly set
    // This ensures that when the user changes the series, we use the new value immediately
    if (config?.selectedSeries && typeof config.selectedSeries === 'string') {
      // If the selected series exists in availableSeries, use it and update ref
      if (availableSeries.includes(config.selectedSeries)) {
        lastValidSelectedSeriesRef.current = config.selectedSeries;
        return config.selectedSeries;
      }
      // If config has a selectedSeries but it's not in availableSeries yet,
      // use the config value anyway (data might still be loading/processing)
      // This prevents reverting to old value when availableSeries temporarily changes
      return config.selectedSeries;
    }
    // Default to first available series if none selected in config
    const defaultSeries = availableSeries.length > 0 ? availableSeries[0] : null;
    if (defaultSeries) {
      lastValidSelectedSeriesRef.current = defaultSeries;
    }
    // If we have a last valid series but no config and no available, use last valid
    return defaultSeries || lastValidSelectedSeriesRef.current || null;
  }, [config?.selectedSeries, availableSeries]);

  // Filter data based on selected series
  // For utilization forecasts, the API already filters by series_type in params,
  // so we should show all data that comes back (no grouping filter needed)
  // For cost forecasts or when filtering by resource, we filter by grouping
  const transformedData = useMemo(() => {
    if (!allData || allData.length === 0) return [];
    
    const forecastType = config?.forecastType;
    const hasProductSeries = config?.dataSource?.params?.series;
    
    // For utilization forecasts with a product series selected, don't filter by grouping
    // The API already filtered by series_type, so show all returned data
    if (forecastType === "utilization" && hasProductSeries) {
      return allData;
    }
    
    // For cost forecasts or when no product series is set, filter by grouping (resource name)
    if (!selectedSeries) return [];
    
    // Filter data to only include the selected series (resource name)
    return allData.filter((d) => {
      const grouping = d.grouping || 'Total';
      return grouping === selectedSeries;
    });
  }, [allData, selectedSeries, config?.forecastType, config?.dataSource?.params?.series]);

  // Handler for date grouping changes from ForecastChartComponent
  const handleDateGroupingChange = React.useCallback((newGrouping) => {
    if (newGrouping && typeof newGrouping === 'object') {
      setDateGrouping(newGrouping);
    }
  }, []);

  // Calculate forecast date range based on showPreviousMonth and showNextMonth settings
  const forecastDateRange = useMemo(() => {
    const now = dayjs();
    const showPreviousMonth = config?.showPreviousMonth !== false;
    const showNextMonth = config?.showNextMonth !== false;
    
    // Start date: previous month start if shown, otherwise current month start
    const startDate = showPreviousMonth 
      ? now.subtract(1, 'month').startOf('month')
      : now.startOf('month');
    
    // End date: next month end if shown, otherwise current month end
    const endDate = showNextMonth
      ? now.add(1, 'month').endOf('month')
      : now.endOf('month');
    
    return {
      startDate: startDate,
      endDate: endDate,
      formatted: `${startDate.format("MMM D, YYYY")} - ${endDate.format("MMM D, YYYY")}`
    };
  }, [config?.showPreviousMonth, config?.showNextMonth]);

  // Check if forecast type and product are selected
  const hasForecastConfig = config?.forecastType && config?.selectedProduct?.value;

  // Generate default title with series if available
  // Only use this if config.title is not explicitly set by the user
  const defaultTitle = useMemo(() => {
    if (!config?.selectedProduct?.label) {
      return "Forecast";
    }
    
    const resourceName = config.selectedProduct.label;
    const forecastType = config?.forecastType;
    
    // For cost forecasts, always use "Forecast for resource" format (no series)
    if (forecastType === "cost") {
      const titleForCost = `Forecast for ${resourceName}`;
      return titleForCost;
    }
    
    // For utilization forecasts, include series if available
    // For the title, we need the product series (cpu, storage, memory), not the chart grouping series (product names)
    // config.selectedSeries might be overwritten by chart grouping logic, so we use dataSource.params.series
    // which is set when the user selects a series from the dropdown in the config sidebar
    // Fall back to selectedProduct.series if params.series is not available
    const productSeries = String(
      config?.dataSource?.params?.series || 
      config?.selectedProduct?.series || 
      ""
    ).trim();
    
    // For utilization forecasts, include series if available
    if (forecastType === "utilization" && productSeries !== "") {
      const titleWithSeries = `Forecast for ${resourceName} - ${formatSeriesName(productSeries)} utilization`;
      return titleWithSeries;
    }
    
    // Default: show without series
    const titleWithoutSeries = `Forecast for ${resourceName}`;
    return titleWithoutSeries;
  }, [config?.selectedProduct?.label, config?.selectedProduct?.series, config?.dataSource?.params?.series, config?.forecastType]);

  // Custom title should be initialized with defaultTitle
  // Only when it's explicitly changed (different from defaultTitle) should it be considered custom
  // Never use the title/subtitle combination from forecast.js ("Forecast for a specific resource")
  // If config.title is empty/undefined, treat it as default (use defaultTitle)
  // If config.title matches defaultTitle, treat it as default (use defaultTitle)
  // If config.title is "Forecast" or "Forecast for a specific resource" (from forecast.js), treat as default (use defaultTitle)
  // If config.title is set and different from defaultTitle, treat it as custom (use config.title)

  // Known default/placeholder titles that shouldn't be treated as custom
  const DEFAULT_TITLE_PATTERNS = [
    "Forecast",
    "Forecast for a specific resource", // Legacy default from forecast.js
  ];

  const configTitle = config?.title ? String(config.title).trim() : "";
  // Title is custom only when it's explicitly set and different from defaultTitle and not the forecast.js default
  const hasCustomTitle = configTitle !== "" && 
    !DEFAULT_TITLE_PATTERNS.includes(configTitle) &&
    configTitle !== defaultTitle;
    
  const displayTitle = hasCustomTitle ? configTitle : defaultTitle;

  return (
    <BaseWidget
      widgetId={widgetId}
      title={displayTitle}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      isEditMode={isEditMode}
    >
      <div 
        className="h-full flex flex-col"
        style={{ minHeight: '200px', height: '100%' }}
      >
        {!hasForecastConfig ? (
          <div className="flex-1 flex items-center justify-center text-text-2 text-sm">
            Please select a forecast type and product in the widget settings.
          </div>
        ) : isLoading ? (
          <div className="flex-1 flex items-center justify-center text-text-2 text-sm">
            Loading forecast data...
          </div>
        ) : allData.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-text-2 text-sm">
            No forecast data available.
          </div>
        ) : transformedData.length > 0 ? (
          <div className="flex-1 flex flex-col h-full">
            <ForecastChartComponent
              data={transformedData}
              dateFormat={dateGrouping.value}
              dateGrouping={dateGrouping}
              setDateGrouping={handleDateGroupingChange}
              title={displayTitle}
              subtitle={forecastDateRange.formatted}
              selectedSeries={selectedSeries}
              valueName={config.valueName || "Total Costs"}
              showButtons={config.showButtons !== false}
              stacked={config.stacked !== false}
              includeForecast={true}
              useOwnBackground={false}
              showTotals={config.showTotals !== false}
              showPreviousMonth={config.showPreviousMonth !== false}
              showNextMonth={config.showNextMonth !== false}
              forecastType={config.forecastType}
            />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-text-2 text-sm">
            No data available for the selected series.
          </div>
        )}
      </div>
    </BaseWidget>
  );
};

ForecastChartWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  config: PropTypes.shape({
    title: PropTypes.string.isRequired,
    dateFormat: PropTypes.string,
    dateGrouping: PropTypes.object,
    showButtons: PropTypes.bool,
    stacked: PropTypes.bool,
    valueName: PropTypes.string,
    selectedSeries: PropTypes.string,
    dataSource: PropTypes.shape({
      type: PropTypes.oneOf(['static', 'api']).isRequired,
      data: PropTypes.any,
      endpoint: PropTypes.string,
      method: PropTypes.string,
      body: PropTypes.object,
      transform: PropTypes.func
    }).isRequired
  }).isRequired,
  filters: PropTypes.array,
  onRemove: PropTypes.func,
  isEditMode: PropTypes.bool,
  onFilterAdd: PropTypes.func
};

// Export availableSeries for use in config sidebar
ForecastChartWidget.getAvailableSeries = (data) => {
  if (!data || !data.CloudCost || !data.CloudCost.details) return [];
  
  const details = data.CloudCost.details.map((d) => {
    if (d.groupings && Array.isArray(d.groupings)) {
      return d.groupings[1]?.value || d.grouping || 'Total';
    }
    return d.grouping || 'Total';
  });
  
  const grouped = _.groupBy(details);
  return Object.keys(grouped).sort();
};

export default ForecastChartWidget;

